import React, {useEffect, useRef, useState} from 'react'
import AdyenCheckout from '@adyen/adyen-web'
import '@adyen/adyen-web/dist/adyen.css'
import useAdyenCheckout from '../../../hooks/useAdyenCheckout'
import {useCurrentBasket} from '@app/hooks/use-current-basket'
import {Spinner, Flex} from '@chakra-ui/react'
import PropTypes from 'prop-types'

export const getCheckoutConfig = (
    adyenEnvironment,
    adyenPaymentMethods,
    paymentMethodsConfiguration,
    translations,
    locale
) => {
    const checkoutConfig = {
        environment: adyenEnvironment?.ADYEN_ENVIRONMENT,
        clientKey: adyenEnvironment?.ADYEN_CLIENT_KEY,
        paymentMethodsResponse: adyenPaymentMethods,
        paymentMethodsConfiguration: paymentMethodsConfiguration,
        locale: locale.id
    }
    if (translations) {
        checkoutConfig.translations = translations
    }
    return checkoutConfig
}

export const handleQueryParams = (
    urlParams,
    checkout,
    setAdyenPaymentInProgress,
    paymentContainer
) => {
    const redirectResult = urlParams.get('redirectResult')
    const amazonCheckoutSessionId = urlParams.get('amazonCheckoutSessionId')
    const adyenAction = urlParams.get('adyenAction')

    if (redirectResult) {
        checkout.submitDetails({data: {details: {redirectResult}}})
    } else if (amazonCheckoutSessionId) {
        setAdyenPaymentInProgress(true)
        const amazonPayContainer = document.createElement('div')
        const amazonPay = checkout
            .create('amazonpay', {
                amazonCheckoutSessionId,
                showOrderButton: false
            })
            .mount(amazonPayContainer)
        amazonPay.submit()
    } else if (adyenAction) {
        const actionString = atob(adyenAction)
        const action = JSON.parse(actionString)
        checkout.createFromAction(action).mount(paymentContainer.current)
    } else {
        checkout.create('dropin').mount(paymentContainer.current)
        if (paymentContainer.current.innerHTML) {
            checkout.update()
        }
    }
}

const AdyenCheckoutComponent = (props) => {
    const {
        adyenEnvironment,
        adyenPaymentMethods,
        getPaymentMethodsConfiguration,
        setAdyenStateData,
        getTranslations,
        locale,
        adyenPaymentInProgress,
        setAdyenPaymentInProgress,
        navigate
    } = useAdyenCheckout()
    const {data: basket} = useCurrentBasket()
    const paymentContainer = useRef(null)

    const [adyenCheckout, setAdyenCheckout] = useState(null)

    useEffect(() => {
        createCheckout()

        // Hoisted declarations below
        async function createCheckout() {
            const paymentMethodsConfiguration = await getPaymentMethodsConfiguration({
                ...props,
                adyenPaymentMethods
            })
            const translations = getTranslations()
            const checkoutConfig = getCheckoutConfig(
                adyenEnvironment,
                adyenPaymentMethods,
                paymentMethodsConfiguration,
                translations,
                locale
            )

            const checkout = await AdyenCheckout({
                ...checkoutConfig,
                onSubmit(state, element) {
                    const onSubmit =
                        paymentMethodsConfiguration.onSubmit ||
                        paymentMethodsConfiguration.card.onSubmit
                    onSubmit(state, element)
                },
                onAdditionalDetails(state, element) {
                    const onAdditionalDetails =
                        paymentMethodsConfiguration.onAdditionalDetails ||
                        paymentMethodsConfiguration.card.onAdditionalDetails
                    onAdditionalDetails(state, element)
                },
                onChange: (state) => {
                    if (state.isValid) {
                        setAdyenStateData(state.data)
                    }
                },
                onError: (error) => {
                    // binLookup errors are handled by the card component, not checkout error
                    if (error?.errorType === 'binLookup') {
                        return
                    }
                    navigate('/checkout/error')
                }
            })

            setAdyenCheckout(checkout)
        }
    }, [])

    useEffect(() => {
        if (!adyenCheckout) return

        const urlParams = new URLSearchParams(location.search)
        const createCheckout = async () => {
            handleQueryParams(urlParams, adyenCheckout, setAdyenPaymentInProgress, paymentContainer)
        }
        if (adyenEnvironment && paymentContainer.current && !adyenPaymentInProgress) {
            // TODO: temporary solution, to be removed or updated after it's fixed in the adyen-web package
            if (window?.paypal?.firstElementChild) {
                window.paypal = undefined
            }

            createCheckout()
        }
    }, [adyenEnvironment, adyenPaymentMethods, adyenCheckout])

    useEffect(() => {
        if (!adyenCheckout) return

        updatePayment()

        // Hoisted declarations below
        async function updatePayment() {
            const paymentMethodsConfiguration = await getPaymentMethodsConfiguration({
                ...props,
                adyenPaymentMethods
            })
            adyenCheckout.update({
                paymentMethodsConfiguration,
                paymentMethodsResponse: adyenPaymentMethods
            })
        }
    }, [basket?.c_basketTotalWithGiftCertificatesApplied, adyenCheckout, adyenPaymentMethods])

    return (
        <>
            {props.showLoading && (
                <Flex align={'center'} justify={'center'}>
                    <Spinner size={'lg'} mt={4} />
                </Flex>
            )}
            <div ref={paymentContainer}></div>
        </>
    )
}

AdyenCheckoutComponent.propTypes = {
    showLoading: PropTypes.bool
}

export default AdyenCheckoutComponent
